// IE Hub - Profile Service
// Provides centralized API operations for profile data integration and transformation
// Handles personal details, contact, address, employment, partner & household, and circumstances

import { httpGet, httpPatch, httpPost } from '../http';
import { budgetEndpoints, customerEndpoints } from '../endpoints';
import { femaCapture } from '../../fema/capture';
import { coerceConsentToHuman } from '../../lib/utils/consent';
import type {
  ApiResponse,
  ButtonActionLogRequest,
  CustomerConsent,
  CustomerProfileData,
  GuidanceNoteRecord,
  ProfileLookupsResponse,
  RequestConfig,
  ServiceResponse,
} from '../types';

type ConsentCarrier = { field4TxtFld?: unknown };

type ApiEnvelope<T> = {
  success: boolean;
  data?: T;
  message?: string;
  error?: ApiResponse<unknown>['error'];
  timestamp?: string;
};

const nowIso = (): string => new Date().toISOString();

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const isApiEnvelope = <T>(value: unknown): value is ApiEnvelope<T> => {
  if (!isRecord(value)) return false;
  if (!('success' in value)) return false;
  return typeof (value as Record<string, unknown>).success === 'boolean';
};

const unwrapLatestRecord = <T>(value: unknown): T | null => {
  if (Array.isArray(value)) {
    if (value.length === 0) return null;
    const last = value[value.length - 1];
    if (last && typeof last === 'object') {
      return last as T;
    }
    return null;
  }

  if (value && typeof value === 'object') {
    return value as T;
  }

  return null;
};

const ok = <T>(data: T, message?: string): ApiResponse<T> => ({
  success: true,
  data,
  ...(typeof message === 'string' && message.trim().length > 0 ? { message } : {}),
  timestamp: nowIso(),
});

const fail = <T>(
  code: string,
  message: string,
  details?: Record<string, unknown>
): ApiResponse<T> => ({
  success: false,
  message,
  error: {
    code,
    message,
    ...(details ? { details: details as Record<string, any> } : {}),
  },
  timestamp: nowIso(),
});

const captureInfo = (type: string, context: Record<string, unknown> = {}): void => {
  femaCapture(type, type, {
    category: 'profile',
    severity: 'info',
    context,
    skipSanitization: false,
    user: {},
  });
};

const captureWarn = (type: string, context: Record<string, unknown> = {}): void => {
  femaCapture(type, type, {
    category: 'profile',
    severity: 'warning',
    context,
    skipSanitization: false,
    user: {},
  });
};

const captureErr = (
  type: string,
  error: unknown,
  context: Record<string, unknown> = {}
): void => {
  femaCapture(type, error, {
    category: 'profile',
    severity: 'error',
    context,
    skipSanitization: false,
    user: {},
  });
};

const normalizeConsentField = <T extends ConsentCarrier>(
  record: T | null | undefined
): T | undefined => {
  if (!record) {
    return undefined;
  }

  if (!Object.prototype.hasOwnProperty.call(record, 'field4TxtFld')) {
    return record;
  }

  const normalized = coerceConsentToHuman(record.field4TxtFld);

  if (normalized !== undefined) {
    return { ...record, field4TxtFld: normalized } as T;
  }

  if (record.field4TxtFld === '' || record.field4TxtFld === null) {
    return { ...record, field4TxtFld: '' } as T;
  }

  return record;
};

const prepareConsentPatch = <T extends ConsentCarrier>(patch: T): T => {
  if (!Object.prototype.hasOwnProperty.call(patch, 'field4TxtFld')) {
    return patch;
  }

  const value = (patch as Record<string, unknown>).field4TxtFld;

  if (value === '' || value === null) {
    return { ...patch, field4TxtFld: '' } as T;
  }

  // PATCH contract expects human-readable consent token (Yes/No).
  // Accept legacy/human input, but always send normalized human value.
  const normalizedHuman = coerceConsentToHuman(value);
  if (normalizedHuman !== undefined) {
    return { ...patch, field4TxtFld: normalizedHuman } as T;
  }

  return patch;
};

/**
 * Profile Service Class
 * Handles profile data aggregation, transformation, and API integrations
 */
type CacheKeyKind = 'customer' | 'user';

export class ProfileService {
  // Cache for performance optimization
  private static profileCache = new Map<string, { data: any; timestamp: number }>();
  private static lookupsCache = new Map<string, { data: any; timestamp: number }>();
  private static userToCustomerMap = new Map<string, string>();
  private static customerToUserMap = new Map<string, string>();
  private static readonly CACHE_TTL = 5 * 60 * 1000; // 5 minutes for profile data
  private static readonly LOOKUPS_CACHE_TTL = 30 * 60 * 1000; // 30 minutes for lookups

  private static buildCacheKey(kind: CacheKeyKind, id: string): string {
    return `${kind}:${id}`;
  }

  private static normalizeId(value?: string | number | null): string | null {
    if (typeof value === 'number' && Number.isFinite(value)) {
      return value.toString();
    }
    if (typeof value === 'string') {
      const trimmed = value.trim();
      return trimmed.length > 0 ? trimmed : null;
    }
    return null;
  }

  private static extractCustomerId(payload: unknown): string | number | undefined {
    if (!payload) {
      return undefined;
    }
    if (Array.isArray(payload)) {
      for (const entry of payload) {
        const candidate = this.extractCustomerId(entry);
        if (candidate !== undefined) {
          return candidate;
        }
      }
      return undefined;
    }
    if (typeof payload === 'object') {
      const record = payload as Record<string, unknown>;
      const candidate =
        record.customerId ??
        record.customerID ??
        record.customer_id ??
        record.customerid ??
        record.id;
      if (typeof candidate === 'string' || typeof candidate === 'number') {
        return candidate;
      }
    }
    return undefined;
  }

  private static cacheProfileData(
    data: any,
    meta: { customerId?: string | number; userId?: string | number } = {}
  ): void {
    if (!data) return;
    const timestamp = Date.now();

    const resolvedCustomerId =
      this.normalizeId(meta.customerId ?? this.extractCustomerId(data)) ?? null;
    const resolvedUserId = this.normalizeId(meta.userId ?? (Array.isArray(data) ? undefined : (data?.userId as string | number | undefined))) ?? null;

    if (resolvedCustomerId) {
      this.profileCache.set(
        this.buildCacheKey('customer', resolvedCustomerId),
        { data, timestamp }
      );
    }

    if (resolvedUserId) {
      this.profileCache.set(
        this.buildCacheKey('user', resolvedUserId),
        { data, timestamp }
      );
    }

    if (resolvedCustomerId && resolvedUserId) {
      this.userToCustomerMap.set(resolvedUserId, resolvedCustomerId);
      this.customerToUserMap.set(resolvedCustomerId, resolvedUserId);
    }
  }

  private static invalidateProfileCache(meta: {
    customerId?: string | number;
    userId?: string | number;
  } = {}): void {
    const normalizedCustomerId = this.normalizeId(meta.customerId);
    const normalizedUserId = this.normalizeId(meta.userId);

    if (normalizedCustomerId) {
      this.profileCache.delete(
        this.buildCacheKey('customer', normalizedCustomerId)
      );
      const mappedUserId = this.customerToUserMap.get(normalizedCustomerId);
      if (mappedUserId) {
        this.profileCache.delete(this.buildCacheKey('user', mappedUserId));
        this.userToCustomerMap.delete(mappedUserId);
        this.customerToUserMap.delete(normalizedCustomerId);
      }
    }

    if (normalizedUserId) {
      this.profileCache.delete(this.buildCacheKey('user', normalizedUserId));
      const mappedCustomerId = this.userToCustomerMap.get(normalizedUserId);
      if (mappedCustomerId) {
        this.profileCache.delete(
          this.buildCacheKey('customer', mappedCustomerId)
        );
        this.customerToUserMap.delete(mappedCustomerId);
        this.userToCustomerMap.delete(normalizedUserId);
      }
    }
  }

  /**
   * Get comprehensive customer profile data
   * @param customerId - The customer ID
   * @param config - Optional request configuration
   */
  static async getCustomerProfile(
    customerId: string | number,
    config?: RequestConfig
  ): ServiceResponse<CustomerProfileData> {
    try {
      const normalizedCustomerId = this.normalizeId(customerId);
      const cacheKey = normalizedCustomerId
        ? this.buildCacheKey('customer', normalizedCustomerId)
        : null;
      const cached = cacheKey ? this.profileCache.get(cacheKey) : undefined;
      const now = Date.now();

      // Check cache first
      if (cached && now - cached.timestamp < this.CACHE_TTL) {
        captureInfo('profile_cache_hit', { customerId });
        return ok<CustomerProfileData>(cached.data);
      }

      const response = await httpGet<ApiEnvelope<CustomerProfileData> | CustomerProfileData>(
        customerEndpoints.getCustomerProfile(customerId),
        config
      );

      const body = response.data;
      if (isApiEnvelope<CustomerProfileData>(body)) {
        const ts = typeof body.timestamp === 'string' ? body.timestamp : nowIso();
        if (body.success && body.data) {
          const normalizedData =
            normalizeConsentField(body.data) ?? body.data;

          // Cache successful response
          this.cacheProfileData(normalizedData, { customerId });

          captureInfo('profile_api_success', { customerId });
          return {
            success: true,
            data: normalizedData,
            ...(typeof body.message === 'string' && body.message.trim().length > 0
              ? { message: body.message }
              : {}),
            timestamp: ts,
          };
        }

        captureWarn('profile_api_no_data', { customerId });
        return {
          success: false,
          message: body.message ?? 'No profile data received',
          error: body.error ?? { code: 'PROFILE_NO_DATA', message: body.message ?? 'No profile data received' },
          timestamp: ts,
        };
      }

      // Non-enveloped responses (legacy environments): treat body as the data payload.
      if (body) {
        const normalizedFallback =
          normalizeConsentField(body) ?? body;

        this.cacheProfileData(normalizedFallback, { customerId });

        captureInfo('profile_api_success', { customerId });
        return ok<CustomerProfileData>(normalizedFallback);
      }

      captureWarn('profile_api_no_data', { customerId });
      return fail<CustomerProfileData>('PROFILE_NO_DATA', 'No profile data received');

    } catch (error: unknown) {
      captureErr('profile_api_error', error, { customerId });
      const msg =
        error instanceof Error
          ? error.message
          : 'Failed to fetch profile data';
      return fail<CustomerProfileData>('PROFILE_API_ERROR', msg);
    }
  }

  /**
   * Update customer profile data with partial fields
   * @param customerId - The customer ID
   * @param patch - Partial profile data to update
   * @param config - Optional request configuration
   */
  static async patchCustomerProfile(
    customerId: string | number,
    patch: Partial<CustomerProfileData>,
    config?: RequestConfig
  ): ServiceResponse<CustomerProfileData> {
    try {
      // Invalidate cache before update
      this.invalidateProfileCache({ customerId });

      const requestPayload = prepareConsentPatch(patch);

      const response = await httpPatch<ApiEnvelope<CustomerProfileData> | CustomerProfileData>(
        customerEndpoints.patchCustomerProfile(customerId),
        requestPayload,
        config
      );

      const body = response.data;
      if (isApiEnvelope<CustomerProfileData>(body)) {
        const ts = typeof body.timestamp === 'string' ? body.timestamp : nowIso();
        if (body.success && body.data) {
          const normalized =
            normalizeConsentField(body.data) ?? body.data;

          this.cacheProfileData(normalized, { customerId });

          captureInfo('profile_patch_success', { customerId });
          return {
            success: true,
            data: normalized,
            ...(typeof body.message === 'string' && body.message.trim().length > 0
              ? { message: body.message }
              : {}),
            timestamp: ts,
          };
        }

        captureWarn('profile_patch_no_data', { customerId });
        return {
          success: false,
          message: body.message ?? 'No profile data returned after update',
          error: body.error ?? { code: 'PROFILE_PATCH_NO_DATA', message: body.message ?? 'No profile data returned after update' },
          timestamp: ts,
        };
      }

      if (body) {
        const normalized = normalizeConsentField(body) ?? body;

        this.cacheProfileData(normalized, { customerId });

        captureInfo('profile_patch_success', { customerId });
        return ok<CustomerProfileData>(normalized);
      }

      captureWarn('profile_patch_no_data', { customerId });
      return fail<CustomerProfileData>('PROFILE_PATCH_NO_DATA', 'No profile data returned after update');

    } catch (error: unknown) {
      captureErr('profile_patch_error', error, { customerId });

      // If conflict (409), try to refetch and reconcile
      if (typeof error === 'object' && error !== null && 'status' in (error as Record<string, unknown>) && (error as Record<string, unknown>).status === 409) {
        captureWarn('profile_patch_conflict', { customerId });
        return fail<CustomerProfileData>(
          'PROFILE_PATCH_CONFLICT',
          'Profile data was modified by another session. Please refresh and try again.'
        );
      }

      const msg =
        error instanceof Error
          ? error.message
          : 'Failed to update profile data';
      return fail<CustomerProfileData>('PROFILE_PATCH_ERROR', msg);
    }
  }

  /**
   * Update customer profile data with partial fields
   * @param customerId - The customer ID
   * @param patch - Partial profile data to update
   * @param config - Optional request configuration
   */
  static async patchCustomerData(
    customerId: string | number,
    patch: Partial<CustomerProfileData>,
    config?: RequestConfig
  ): ServiceResponse<CustomerProfileData> {
    try {
      // Invalidate cache before update
      this.invalidateProfileCache({ customerId });

      const requestPayload = prepareConsentPatch(patch);

      const response = await httpPatch<ApiEnvelope<CustomerProfileData> | CustomerProfileData>(
        customerEndpoints.patchCustomerData(customerId),
        requestPayload,
        config
      );

      const body = response.data;
      if (isApiEnvelope<CustomerProfileData>(body)) {
        const ts = typeof body.timestamp === 'string' ? body.timestamp : nowIso();
        if (body.success && body.data) {
          const normalized =
            normalizeConsentField(body.data) ?? body.data;

          this.cacheProfileData(normalized, { customerId });

          captureInfo('profile_patch_success', { customerId });
          return {
            success: true,
            data: normalized,
            ...(typeof body.message === 'string' && body.message.trim().length > 0
              ? { message: body.message }
              : {}),
            timestamp: ts,
          };
        }

        captureWarn('profile_patch_no_data', { customerId });
        return {
          success: false,
          message: body.message ?? 'No profile data returned after update',
          error: body.error ?? { code: 'PROFILE_PATCH_NO_DATA', message: body.message ?? 'No profile data returned after update' },
          timestamp: ts,
        };
      }

      if (body) {
        const normalized = normalizeConsentField(body) ?? body;

        this.cacheProfileData(normalized, { customerId });

        captureInfo('profile_patch_success', { customerId });
        return ok<CustomerProfileData>(normalized);
      }

      captureWarn('profile_patch_no_data', { customerId });
      return fail<CustomerProfileData>('PROFILE_PATCH_NO_DATA', 'No profile data returned after update');

    } catch (error: unknown) {
      captureErr('profile_patch_error', error, { customerId });

      // If conflict (409), try to refetch and reconcile
      if (typeof error === 'object' && error !== null && 'status' in (error as Record<string, unknown>) && (error as Record<string, unknown>).status === 409) {
        captureWarn('profile_patch_conflict', { customerId });
        return fail<CustomerProfileData>(
          'PROFILE_PATCH_CONFLICT',
          'Profile data was modified by another session. Please refresh and try again.'
        );
      }

      const msg =
        error instanceof Error
          ? error.message
          : 'Failed to update profile data';
      return fail<CustomerProfileData>('PROFILE_PATCH_ERROR', msg);
    }
  }

   /**
   * Get comprehensive customer profile data
   * @param userId - The customer ID
   * @param config - Optional request configuration
   */
  static async getCustomerData(
    userId: string | number,
    config?: RequestConfig
  ): ServiceResponse<CustomerProfileData> {
    try {
      const skipCache = config?.skipCache === true;
      const normalizedUserId = this.normalizeId(userId);
      const cacheKey = normalizedUserId
        ? this.buildCacheKey('user', normalizedUserId)
        : null;
      const cached = !skipCache && cacheKey
        ? this.profileCache.get(cacheKey)
        : undefined;
      const now = Date.now();

      // Check cache first
      if (cached && now - cached.timestamp < this.CACHE_TTL) {
        captureInfo('profile_cache_hit', { userId });
        return ok<CustomerProfileData>(cached.data);
      }

      const response = await httpGet<
        ApiEnvelope<CustomerProfileData | CustomerProfileData[]> | CustomerProfileData | CustomerProfileData[]
      >(
        customerEndpoints.getCustomerData(userId),
        config
      );

      const body = response.data;
      if (isApiEnvelope<CustomerProfileData | CustomerProfileData[]>(body)) {
        const ts = typeof body.timestamp === 'string' ? body.timestamp : nowIso();
        if (body.success && body.data) {
          const record = unwrapLatestRecord<CustomerProfileData>(body.data);
          if (!record) {
            captureWarn('profile_api_no_data', { customerId: userId });
            return {
              success: false,
              message: body.message ?? 'No profile data received',
              error:
                body.error ??
                { code: 'PROFILE_NO_DATA', message: body.message ?? 'No profile data received' },
              timestamp: ts,
            };
          }

          const normalizedData = normalizeConsentField(record) ?? record;

          const extractedCustomerId = this.extractCustomerId(normalizedData);

          // Cache successful response
          this.cacheProfileData(normalizedData, {
            userId,
            ...(extractedCustomerId !== undefined ? { customerId: extractedCustomerId } : {}),
          });

          captureInfo('profile_api_success', { customerId: userId });
          return {
            success: true,
            data: normalizedData,
            ...(typeof body.message === 'string' && body.message.trim().length > 0
              ? { message: body.message }
              : {}),
            timestamp: ts,
          };
        }

        captureWarn('profile_api_no_data', { customerId: userId });
        return {
          success: false,
          message: body.message ?? 'No profile data received',
          error: body.error ?? { code: 'PROFILE_NO_DATA', message: body.message ?? 'No profile data received' },
          timestamp: ts,
        };
      }

      if (body) {
        const record = unwrapLatestRecord<CustomerProfileData>(body);
        if (!record) {
          captureWarn('profile_api_no_data', { customerId: userId });
          return fail<CustomerProfileData>('PROFILE_NO_DATA', 'No profile data received');
        }

        const normalizedData = normalizeConsentField(record) ?? record;

        const extractedCustomerId = this.extractCustomerId(normalizedData);

        this.cacheProfileData(normalizedData, {
          userId,
          ...(extractedCustomerId !== undefined ? { customerId: extractedCustomerId } : {}),
        });

        captureInfo('profile_api_success', { customerId: userId });
        return ok<CustomerProfileData>(normalizedData);
      }

      captureWarn('profile_api_no_data', { customerId: userId });
      return fail<CustomerProfileData>('PROFILE_NO_DATA', 'No profile data received');

    } catch (error: unknown) {
      captureErr('profile_api_error', error, { customerId: userId });
      const msg =
        error instanceof Error
          ? error.message
          : 'Failed to fetch profile data';
      return fail<CustomerProfileData>('PROFILE_API_ERROR', msg);
    }
  }


  /**
   * Get profile lookup options (accommodation types, employment statuses, etc.)
   * @param config - Optional request configuration
   */
  static async getProfileLookups(
    config?: RequestConfig
  ): ServiceResponse<ProfileLookupsResponse> {
    try {
      const cacheKey = 'profile_lookups';
      const cached = this.lookupsCache.get(cacheKey);
      const now = Date.now();

      // Check cache first (longer TTL for lookups)
      if (cached && (now - cached.timestamp) < this.LOOKUPS_CACHE_TTL) {
        captureInfo('profile_lookups_cache_hit');
        return ok<ProfileLookupsResponse>(cached.data);
      }

      const response = await httpGet<ApiEnvelope<ProfileLookupsResponse> | ProfileLookupsResponse>(
        customerEndpoints.getProfileLookups(),
        config
      );

      const body = response.data;
      if (isApiEnvelope<ProfileLookupsResponse>(body)) {
        const ts = typeof body.timestamp === 'string' ? body.timestamp : nowIso();
        if (body.success && body.data) {
          // Cache successful response
          this.lookupsCache.set(cacheKey, {
            data: body.data,
            timestamp: now
          });

          captureInfo('profile_lookups_api_success');
          return {
            success: true,
            data: body.data,
            ...(typeof body.message === 'string' && body.message.trim().length > 0
              ? { message: body.message }
              : {}),
            timestamp: ts,
          };
        }

        captureWarn('profile_lookups_api_no_data');
        return {
          success: false,
          message: body.message ?? 'No profile lookups received',
          error: body.error ?? { code: 'PROFILE_LOOKUPS_NO_DATA', message: body.message ?? 'No profile lookups received' },
          timestamp: ts,
        };
      }

      if (body) {
        this.lookupsCache.set(cacheKey, {
          data: body,
          timestamp: now
        });

        captureInfo('profile_lookups_api_success');
        return ok<ProfileLookupsResponse>(body);
      }

      captureWarn('profile_lookups_api_no_data');
      return fail<ProfileLookupsResponse>('PROFILE_LOOKUPS_NO_DATA', 'No profile lookups received');

    } catch (error: unknown) {
      captureErr('profile_lookups_api_error', error);
      const msg =
        error instanceof Error
          ? error.message
          : 'Failed to fetch profile lookups';
      return fail<ProfileLookupsResponse>('PROFILE_LOOKUPS_ERROR', msg);
    }
  }

  /**
   * Get guidance notes for profile fields
   * @param config - Optional request configuration
   */
  static async getGuidanceNotes(
    config?: RequestConfig
  ): ServiceResponse<GuidanceNoteRecord[]> {
    try {
      const response = await httpGet<ApiEnvelope<GuidanceNoteRecord[]> | GuidanceNoteRecord[]>(
        budgetEndpoints.getGuidanceNotes(),
        config
      );

      const body = response.data;
      if (isApiEnvelope<GuidanceNoteRecord[]>(body)) {
        const ts = typeof body.timestamp === 'string' ? body.timestamp : nowIso();
        if (body.success && Array.isArray(body.data)) {
          captureInfo('profile_guidance_api_success');
          return {
            success: true,
            ...(typeof body.message === 'string' && body.message.trim().length > 0
              ? { message: body.message }
              : {}),
            data: body.data,
            timestamp: ts,
          };
        }

        captureWarn('profile_guidance_api_no_data');
        return {
          success: false,
          message: body.message ?? 'No guidance notes received',
          error: body.error ?? { code: 'PROFILE_GUIDANCE_NO_DATA', message: body.message ?? 'No guidance notes received' },
          timestamp: ts,
        };
      }

      if (Array.isArray(body)) {
        captureInfo('profile_guidance_api_success');
        return ok<GuidanceNoteRecord[]>(body);
      }

      captureWarn('profile_guidance_api_no_data');
      return fail<GuidanceNoteRecord[]>('PROFILE_GUIDANCE_NO_DATA', 'No guidance notes received');

    } catch (error: unknown) {
      captureErr('profile_guidance_api_error', error);
      const msg =
        error instanceof Error
          ? error.message
          : 'Failed to fetch guidance notes';
      return fail<GuidanceNoteRecord[]>('PROFILE_GUIDANCE_ERROR', msg);
    }
  }

  /**
   * Log button action for analytics
   * @param payload - Button action log data
   * @param config - Optional request configuration
   */
  static async logButtonAction(
    payload: ButtonActionLogRequest,
    config?: RequestConfig
  ): ServiceResponse<void> {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        return { success: true, timestamp: nowIso() };
      }

      // Button action logging is non-blocking - don't fail if it errors
      await httpPost<ApiEnvelope<void> | ApiResponse<void>>(
        customerEndpoints.logButtonAction(),
        payload,
        config
      );

      captureInfo('profile_button_action_logged', {
        pageName: payload.pageName,
        subSection: payload.subSection
      });

      return { success: true, timestamp: nowIso() };

    } catch (error: any) {
      captureErr('profile_button_action_error', error, {
        pageName: payload.pageName,
        subSection: payload.subSection
      });

      // Log errors but don't block the user experience
      console.warn(
        'Failed to log button action:',
        error instanceof Error ? error.message : error
      );
      return { success: true, timestamp: nowIso() }; // Still return success for UX
    }
  }

  /**
   * Clear profile cache (useful for logout or forced refresh)
   */
  static clearCache(): void {
    this.profileCache.clear();
    this.userToCustomerMap.clear();
    this.customerToUserMap.clear();
    this.lookupsCache.clear();
    captureInfo('profile_cache_cleared');
  }

  /**
   * Get cache statistics for debugging
   */
  static getCacheStats() {
    return {
      profileCacheSize: this.profileCache.size,
      lookupsCacheSize: this.lookupsCache.size,
      profileCacheKeys: Array.from(this.profileCache.keys()),
      lookupsCacheKeys: Array.from(this.lookupsCache.keys())
    };
  }

  /**
   * Transform raw profile data into UI-friendly DTO
   * @param profileData - Raw profile data from API
   * @param lookups - Lookup data for code-to-label conversion
   */
  static composeProfileDTO(profileData: CustomerProfileData, lookups?: ProfileLookupsResponse): any {
    if (!profileData) return null;

    return {
      // Personal details
      fullName: this.computeFullName(profileData),
      title: this.getLookupLabel(lookups?.titles, profileData.title),
      firstName: profileData.firstName || profileData.debtor1FirstName,
      surname: profileData.surname || profileData.debtor1LastName,
      dateOfBirth: profileData.dateOfBirth || profileData.debtor1DateOfBirthStr,
      emailId: profileData.emailId || profileData.emailAddress, // Read-only

      // Contact details
      phoneNumber: profileData.phoneNumber || profileData.mobileNumber || profileData.landlineNumber,

      // Address details
      addressDisplay: this.computeAddressDisplay(profileData),
      addressLine1: profileData.addressLine1 || profileData.buildingNumber,
      addressLine2: profileData.addressLine2 || profileData.buildingName,
      city: profileData.city || profileData.postTown,
      postcode: profileData.postcode || profileData.postCode,
      accommodationType: this.getLookupLabel(lookups?.accommodationTypes, profileData.accommodationTypeCode || profileData.housingTenure),

      // Employment details
      employmentStatus: this.getLookupLabel(lookups?.employmentStatuses, profileData.employmentStatusCode || profileData.customerEmploymentStatus),
      isCompletingWithPartner: profileData.isCompletingWithPartner || (profileData.jointAccount === 'YES'),

      // Partner & Household details
      partnerFullName: this.computePartnerFullName(profileData),
      partnerDateOfBirth: profileData.partnerDateOfBirth || profileData.debtor2DateOfBirthStr,
      partnerEmploymentStatus: this.getLookupLabel(lookups?.employmentStatuses, profileData.partnerEmploymentStatusCode || profileData.partnerEmploymentStatus),
      dependantsUnder16: profileData.dependantsUnder16 || profileData.dependantsUnder16Count || profileData.dependentChildrenUnder16,
      vehicles: profileData.vehicles || profileData.vehiclesCount || profileData.noOfVehiclesInHouseholds,

      // Circumstances
      categoriesChosen: this.getCircumstancesLabels(lookups?.circumstances, profileData.circumstanceCategoryCodes),
      consent: profileData.consent || profileData.consentText || profileData.customerConsent,

      // Metadata
      lastUpdateDate: profileData.lastUpdateDate,
      creationDate: profileData.creationDate,
      status: profileData.status
    };
  }

  /**
   * Build minimal PATCH payload from dirty fields
   * @param dirtyFields - Object with only changed fields
   * @param lookups - Lookup data for code conversion
   */
  static toPatchPayload(
    dirtyFields: Partial<CustomerProfileData> & {
      categoriesChosen?: string[];
      consent?: CustomerConsent;
    },
    lookups?: ProfileLookupsResponse
  ): Partial<CustomerProfileData> {
    if (!dirtyFields) return {};

    const patch: Partial<CustomerProfileData> = {};

    // Personal details
    if (dirtyFields.firstName !== undefined) {
      patch.firstName = dirtyFields.firstName;
    }
    if (dirtyFields.surname !== undefined) {
      patch.surname = dirtyFields.surname;
    }
    if (dirtyFields.dateOfBirth !== undefined) {
      patch.dateOfBirth = dirtyFields.dateOfBirth;
    }
    if (dirtyFields.phoneNumber !== undefined) {
      patch.phoneNumber = dirtyFields.phoneNumber;
    }

    // Address details
    if (dirtyFields.addressLine1 !== undefined) {
      patch.addressLine1 = dirtyFields.addressLine1;
    }
    if (dirtyFields.addressLine2 !== undefined) {
      patch.addressLine2 = dirtyFields.addressLine2;
    }
    if (dirtyFields.city !== undefined) {
      patch.city = dirtyFields.city;
    }
    if (dirtyFields.postcode !== undefined) {
      patch.postcode = dirtyFields.postcode;
    }

    // Accommodation type (convert label to code)
    if (dirtyFields.accommodationType !== undefined) {
      const code = this.getLookupCode(lookups?.accommodationTypes, dirtyFields.accommodationType);
      if (code) patch.accommodationTypeCode = code;
    }

    // Employment details
    if (dirtyFields.employmentStatus !== undefined) {
      const code = this.getLookupCode(lookups?.employmentStatuses, dirtyFields.employmentStatus);
      if (code) patch.employmentStatusCode = code;
    }
    if (dirtyFields.isCompletingWithPartner !== undefined) {
      patch.isCompletingWithPartner = dirtyFields.isCompletingWithPartner;
    }

    // Partner & Household details
    if (dirtyFields.partnerFirstName !== undefined) {
      patch.partnerFirstName = dirtyFields.partnerFirstName;
    }
    if (dirtyFields.partnerSurname !== undefined) {
      patch.partnerSurname = dirtyFields.partnerSurname;
    }
    if (dirtyFields.partnerDateOfBirth !== undefined) {
      patch.partnerDateOfBirth = dirtyFields.partnerDateOfBirth;
    }
    if (dirtyFields.partnerEmploymentStatus !== undefined) {
      const code = this.getLookupCode(lookups?.employmentStatuses, dirtyFields.partnerEmploymentStatus);
      if (code) patch.partnerEmploymentStatusCode = code;
    }
    if (dirtyFields.dependantsUnder16 !== undefined) {
      patch.dependantsUnder16 = dirtyFields.dependantsUnder16;
    }
    if (dirtyFields.vehicles !== undefined) {
      patch.vehicles = dirtyFields.vehicles;
    }

    // Circumstances
    if (dirtyFields.categoriesChosen !== undefined) {
      const codes = this.getCircumstancesCodes(lookups?.circumstances, dirtyFields.categoriesChosen);
      if (codes) patch.circumstanceCategoryCodes = codes;
    }
    if (dirtyFields.consent !== undefined) {
      patch.consent = dirtyFields.consent;
    }

    return patch;
  }

  // Private helper methods
  private static computeFullName(profileData: CustomerProfileData): string {
    const title = profileData.title;
    const firstName = profileData.firstName || profileData.debtor1FirstName;
    const surname = profileData.surname || profileData.debtor1LastName;

    if (title && firstName && surname) {
      return `${title} ${firstName} ${surname}`;
    }
    if (firstName && surname) {
      return `${firstName} ${surname}`;
    }
    return '';
  }

  private static computeAddressDisplay(profileData: CustomerProfileData): string {
    const parts = [
      profileData.addressLine1 || profileData.buildingNumber,
      profileData.addressLine2 || profileData.buildingName,
      profileData.city || profileData.postTown,
      profileData.postcode || profileData.postCode
    ].filter(Boolean);

    return parts.join(', ');
  }

  private static computePartnerFullName(profileData: CustomerProfileData): string {
    const firstName = profileData.partnerFirstName;
    const surname = profileData.partnerSurname;

    if (firstName && surname) {
      return `${firstName} ${surname}`;
    }
    if (profileData.debtor2Name) {
      return profileData.debtor2Name;
    }
    return '';
  }

  private static getLookupLabel(lookups?: Array<{ code: string; label: string }>, code?: string): string {
    if (!code || !lookups) return code || '';
    const item = lookups.find(l => l.code === code);
    return item?.label || code;
  }

  private static getLookupCode(lookups?: Array<{ code: string; label: string }>, label?: string): string | null {
    if (!label || !lookups) return null;
    const item = lookups.find(l => l.label === label);
    return item?.code || null;
  }

  private static getCircumstancesLabels(lookups?: Array<{ code: string; label: string }>, codes?: string[]): string[] {
    if (!codes || !lookups) return codes || [];
    return codes.map(code => this.getLookupLabel(lookups, code)).filter(Boolean);
  }

  private static getCircumstancesCodes(lookups?: Array<{ code: string; label: string }>, labels?: string[]): string[] | null {
    if (!labels || !lookups) return null;
    return labels.map(label => this.getLookupCode(lookups, label)).filter(Boolean) as string[];
  }
}
